import React from 'react';
import { Play, Sparkles, Guitar, Music, Star, Heart } from 'lucide-react';
import { soundEngine } from '../utils/audio';

interface HeroProps {
  onExploreVideos: () => void;
  onExploreGuitar: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onExploreVideos, onExploreGuitar }) => {
  const handleVideosClick = () => {
    soundEngine.playPopSfx();
    onExploreVideos();
  };

  const handleGuitarClick = () => {
    soundEngine.playSparkleSfx();
    onExploreGuitar();
  };

  return (
    <section id="home" className="relative pt-8 pb-16 md:pt-12 md:pb-24 overflow-hidden">
      {/* Decorative Kawaii Background Blobs & Floating Elements */}
      <div className="absolute top-10 left-6 text-pink-300 text-3xl animate-float select-none pointer-events-none">🌸</div>
      <div className="absolute top-32 right-12 text-purple-300 text-4xl animate-float-delay select-none pointer-events-none">🎵</div>
      <div className="absolute bottom-16 left-16 text-yellow-300 text-3xl animate-float select-none pointer-events-none">⭐</div>
      <div className="absolute bottom-24 right-20 text-rose-300 text-3xl animate-float-delay select-none pointer-events-none">💖</div>
      <div className="absolute top-1/2 left-2 text-indigo-300 text-2xl animate-float select-none pointer-events-none">🎶</div>

      {/* Soft gradient pastel glow orbs in background */}
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-gradient-to-tr from-pink-200/40 via-purple-200/30 to-rose-200/40 rounded-full blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          
          {/* Left Column: Text & Badges */}
          <div className="lg:col-span-7 text-center lg:text-left space-y-6">
            
            {/* Cute pill badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-100 to-purple-100 border border-pink-200 shadow-xs text-pink-700 font-semibold text-xs sm:text-sm">
              <span className="animate-spin text-base">✨</span>
              <span>Welcome to Melodalis' Musical World</span>
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-ping" />
            </div>

            {/* Main title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-800 leading-tight">
              Where <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-rose-500 bg-clip-text text-transparent">Every Note</span> Smiles & Songs Come Alive!
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg text-purple-900/75 max-w-2xl mx-auto lg:mx-0 font-medium leading-relaxed">
              Step into a colorful kawaii wonderland of sweet melodies, energetic singing, and playful instrument games. Watch Melodalis' debut performance and master your first guitar chords today!
            </p>

            {/* Call to Action Buttons */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
              <button
                onClick={handleVideosClick}
                className="group px-7 py-4 rounded-full bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 text-white font-bold text-base shadow-lg shadow-pink-300/60 hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3 cursor-pointer"
              >
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4 fill-white text-white ml-0.5" />
                </div>
                <span>Watch Debut Video</span>
              </button>

              <button
                onClick={handleGuitarClick}
                className="px-7 py-4 rounded-full bg-white text-purple-700 font-bold text-base border-2 border-purple-200 shadow-md hover:bg-purple-50 hover:border-purple-300 hover:scale-105 active:scale-95 transition-all flex items-center gap-3 cursor-pointer"
              >
                <Guitar className="w-5 h-5 text-purple-500" />
                <span>Play Guitar Games</span>
              </button>
            </div>

            {/* Cute quick features */}
            <div className="grid grid-cols-3 gap-3 pt-4 max-w-lg mx-auto lg:mx-0">
              <div className="p-3 rounded-2xl bg-white/70 border border-pink-100 backdrop-blur-xs text-center shadow-2xs">
                <div className="text-xl mb-1">🎤</div>
                <div className="text-xs font-bold text-gray-700">Official Videos</div>
                <div className="text-[10px] text-pink-600 font-medium">Watch & Sing</div>
              </div>
              <div className="p-3 rounded-2xl bg-white/70 border border-purple-100 backdrop-blur-xs text-center shadow-2xs">
                <div className="text-xl mb-1">🎸</div>
                <div className="text-xs font-bold text-gray-700">Guitar Academy</div>
                <div className="text-[10px] text-purple-600 font-medium">Chords & Notes</div>
              </div>
              <div className="p-3 rounded-2xl bg-white/70 border border-rose-100 backdrop-blur-xs text-center shadow-2xs">
                <div className="text-xl mb-1">🌸</div>
                <div className="text-xs font-bold text-gray-700">Kawaii World</div>
                <div className="text-[10px] text-rose-600 font-medium">melodalis.com</div>
              </div>
            </div>

          </div>

          {/* Right Column: Circular Official Logo Artwork in Kawaii Stage Frame */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative group">
              {/* Outer glowing pulsing ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-pink-400 via-purple-300 to-rose-400 blur-xl opacity-60 group-hover:opacity-80 transition-opacity animate-pulse" />
              
              {/* Floating badges around the logo */}
              <div className="absolute -top-3 -right-2 z-20 px-3.5 py-1.5 rounded-full bg-yellow-300 text-yellow-900 font-bold text-xs shadow-md border-2 border-white flex items-center gap-1 animate-bounce">
                <Star className="w-3.5 h-3.5 fill-current" />
                <span>Young Star!</span>
              </div>

              <div className="absolute -bottom-2 -left-3 z-20 px-3.5 py-1.5 rounded-full bg-pink-500 text-white font-bold text-xs shadow-md border-2 border-white flex items-center gap-1">
                <Heart className="w-3.5 h-3.5 fill-current" />
                <span>Freya Skye Tribute</span>
              </div>

              {/* Central Round Logo Art Container */}
              <div className="relative z-10 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 rounded-full p-2 bg-gradient-to-b from-pink-200 via-white to-purple-200 shadow-2xl border-4 border-white overflow-hidden transition-transform transform group-hover:scale-102">
                <img
                  src="/images/logo-round.jpg"
                  alt="Melodalis Emblem"
                  className="w-full h-full object-cover rounded-full select-none"
                />
              </div>

              {/* Music notes orbiting */}
              <div className="absolute top-1/4 -left-6 z-20 bg-white/90 p-2.5 rounded-2xl shadow-md border border-pink-200 text-pink-500 animate-float">
                <Music className="w-5 h-5" />
              </div>
              <div className="absolute bottom-1/4 -right-4 z-20 bg-white/90 p-2.5 rounded-2xl shadow-md border border-purple-200 text-purple-500 animate-float-delay">
                <Sparkles className="w-5 h-5" />
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

